import { safeEventParameters, safePageParameters } from './analyticsPrivacy.js';
/**
 * Client-side form tracking utilities for Peace Love Wellness.
 *
 * Captures attribution context (UTM params, landing page, referrer) from
 * sessionStorage so it can be included in Netlify form submissions and
 * fired to GA4 as form_start / form_submit events.
 *
 * Design constraints:
 *  - No clinical or personally identifiable data is read or stored here.
 *  - All reads are guarded for SSR safety (typeof window / sessionStorage).
 *  - Referrer is reduced to hostname only to avoid path-level PII leakage.
 *  - GA4 events include attribution fields only — never name, email, or
 *    free-text clinical content from the form.
 */

const SESSION_KEY_LANDING  = 'plw_landing';
const SESSION_KEY_UTM      = 'plw_utm';
const SESSION_KEY_PREV     = 'plw_prev';

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

// Service page paths → readable label for the "Service Interest" field.
// Extend this map as new specialty pages are added.
const SERVICE_PAGE_MAP = {
  '/individual-therapy':                           'Individual Therapy',
  '/couples-relationship-therapy':                 'Couples & Relationship Therapy',
  '/how-we-help':                                  'How We Help',
  '/anxiety-therapy-new-york':                     'Anxiety Therapy',
  '/trauma-therapy-new-york':                      'Trauma Therapy',
  '/emdr-therapy-new-york':                        'EMDR Therapy',
  '/dbt-therapy-new-york':                         'DBT Therapy',
  '/burnout-therapy-new-york':                     'Burnout Therapy',
  '/grief-therapy-new-york':                       'Grief & Loss Therapy',
  '/neurodivergent-affirming-therapy-new-york':    'Neurodivergent-Affirming Therapy',
  '/depression-therapy-new-york':                  'Depression Therapy',
  '/gender-affirming-therapy-new-york':            'Gender-Affirming Therapy',
  '/spanish-speaking-therapy-new-york':            'Spanish-Speaking Therapy',
  '/bilingual-therapy-new-york':                   'Bilingual & Bicultural Therapy',
  '/first-generation-therapy-new-york':            'First-Generation Therapy',
  '/life-transitions-therapy-new-york':            'Life Transitions Therapy',
  '/ocd-therapy-new-york':                         'OCD Therapy',
  '/religious-trauma-therapy-new-york':            'Religious Trauma Therapy',
  '/perinatal-postpartum-therapy-new-york':        'Perinatal & Postpartum Therapy',
  '/self-esteem-therapy-new-york':                 'Self-Esteem Therapy',
  '/online-therapy':                               'Online Therapy',
  '/start-therapy-sooner':                         'Start Therapy Sooner',
  '/couples-therapy-communication-new-york':       'Couples Communication Therapy',
  '/lgbtq-couples-therapy-new-york':               'LGBTQIA+ Couples Therapy',
  '/polyamory-affirming-couples-therapy-new-york': 'Polyamory-Affirming Therapy',
  '/relationship-anxiety-therapy-new-york':        'Relationship Anxiety Therapy',
  '/insurance-fees':                               'Insurance & Fees',
};

function safeSessionSet(key, value) {
  try { sessionStorage.setItem(key, value); } catch { /* storage full or restricted */ }
}

function safeSessionGet(key) {
  try { return sessionStorage.getItem(key); } catch { return null; }
}

/**
 * Call from gatsby-browser.js → onRouteUpdate on every route change.
 * Sets the landing page once per session; captures UTM params whenever
 * they appear in the URL (e.g. a campaign link mid-session overwrites).
 */
export function initSessionTracking(location) {
  if (typeof window === 'undefined') return;

  // Landing page: first page of the session — set once and never overwritten.
  if (!safeSessionGet(SESSION_KEY_LANDING)) {
    safeSessionSet(SESSION_KEY_LANDING, location.pathname);
  }

  // UTM params: capture from current URL if any are present.
  const params = new URLSearchParams(location.search || '');
  const utm = {};
  let hasUtm = false;
  UTM_PARAMS.forEach((key) => {
    const val = params.get(key);
    if (val) { utm[key] = val; hasUtm = true; }
  });
  if (hasUtm) {
    safeSessionSet(SESSION_KEY_UTM, JSON.stringify(utm));
  }
}

/**
 * Call from gatsby-browser.js → onRouteUpdate with the previous location.
 * Stores the path the user navigated FROM so GetStartedPage can infer
 * which clinician profile or service page drove the inquiry.
 */
export function recordPreviousPage(prevPathname) {
  if (typeof window === 'undefined' || !prevPathname) return;
  safeSessionSet(SESSION_KEY_PREV, prevPathname);
}

/**
 * Returns all attribution values to populate hidden form fields and GA4 events.
 * Call inside useEffect (client-side only) on the Get Started page.
 *
 * @returns {{
 *   landing_page: string,
 *   current_page: string,
 *   referrer: string,
 *   utm_source: string,
 *   utm_medium: string,
 *   utm_campaign: string,
 *   utm_content: string,
 *   utm_term: string,
 *   clinician_from: string,   // slug, e.g. "cameron-eshgh"
 *   service_interest: string, // readable label, e.g. "Anxiety Therapy"
 * }}
 */
export function getFormTrackingValues() {
  if (typeof window === 'undefined') return {};

  // UTM params stored during session
  let utm = {};
  try { utm = JSON.parse(safeSessionGet(SESSION_KEY_UTM) || '{}'); } catch { /* */ }

  const landingPage = safeSessionGet(SESSION_KEY_LANDING) || window.location.pathname;
  const currentPage = window.location.pathname;

  // Referrer: external hostname only — no path or query to avoid PII leakage.
  let referrer = '';
  try {
    if (document.referrer) {
      const ref = new URL(document.referrer);
      if (ref.hostname !== window.location.hostname) {
        referrer = ref.hostname;
      }
    }
  } catch { /* invalid URL */ }

  // Clinician context: did the user come from a clinician profile page?
  const prevPage = safeSessionGet(SESSION_KEY_PREV) || '';
  const clinicianMatch = prevPage.match(/^\/our-clinicians\/([a-z0-9-]+)$/);
  const clinicianFrom = clinicianMatch ? clinicianMatch[1] : '';

  // Service context: did the user come from a service/specialty page?
  const serviceInterest = SERVICE_PAGE_MAP[prevPage] || '';

  return {
    landing_page:   landingPage,
    current_page:   currentPage,
    referrer,
    utm_source:     utm.utm_source    || '',
    utm_medium:     utm.utm_medium    || '',
    utm_campaign:   utm.utm_campaign  || '',
    utm_content:    utm.utm_content   || '',
    utm_term:       utm.utm_term      || '',
    clinician_from: clinicianFrom,
    service_interest: serviceInterest,
  };
}

/**
 * Fires a GA4 event via window.gtag. No-ops silently if gtag is not loaded.
 * Only controlled form event metadata and sanitized path-level URLs are sent.
 */
export function fireGtagEvent(eventName) {
  if (typeof window === 'undefined') return;
  try {
    if (window.gtag) window.gtag('event', eventName, { ...safeEventParameters(eventName), ...safePageParameters(window.location, document.referrer) });
  } catch { /* swallow to prevent tracking from breaking form flow */ }
}
